import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, RefreshCw } from 'lucide-react';
import { haptic } from '@/utils/haptics';

/**
 * Session Recovery Toast
 * 
 * Shows a gentle, dismissible toast for users with incomplete sessions:
 * - Plan started but not completed
 * - Cart generated but not exported
 * 
 * Respects:
 * - 14 day session window
 * - 30 day dismissal cooldown
 * - Auto-dismiss after 12 seconds
 */
const SessionRecoveryToast = () => {
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = async () => {
      try {
        const res = await api.get('/user/session-status');
        
        if (res.data.show_toast) {
          setSessionData(res.data);
          // Small delay before showing for smoother UX
          setTimeout(() => setVisible(true), 1500);
        }
      } catch (error) {
        console.error('Failed to check session status:', error);
      }
    };

    checkSession();
  }, [api, isAuthenticated]);

  // Define handleDismiss before the useEffect that uses it
  const handleDismiss = useCallback(async (persist = true) => {
    setIsExiting(true);
    haptic('light');
    
    // Wait for exit animation
    setTimeout(() => {
      setVisible(false);
      setIsExiting(false);
    }, 300);

    // Persist dismissal to backend (30 day cooldown)
    if (persist) {
      try {
        await api.post('/user/dismiss-session-toast');
      } catch (error) {
        console.error('Failed to persist toast dismissal:', error);
      }
    }
  }, [api]);

  // Auto-dismiss after 12 seconds
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      handleDismiss(false); // Don't persist dismissal for auto-dismiss
    }, 12000);

    return () => clearTimeout(timer);
  }, [visible, handleDismiss]);

  const handleContinue = () => {
    haptic('medium');
    navigate(sessionData?.resume_url || '/meal-planner');
    setVisible(false);
  };

  const handleStartNew = () => {
    haptic('light');
    navigate('/dashboard');
    handleDismiss(true);
  };

  if (!visible || !sessionData) return null;

  return (
    <div 
      className={`fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      data-testid="session-recovery-toast"
    >
      <div className="bg-card border border-border/50 rounded-xl shadow-lg p-4 relative">
        {/* Dismiss Button */}
        <button
          onClick={() => handleDismiss(true)}
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
          aria-label="Dismiss"
          data-testid="toast-dismiss"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Content */}
        <div className="pr-8">
          <p className="font-medium text-foreground mb-1">
            Pick up where you left off
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {sessionData.status === 'plan_incomplete' ? (
              <>You were building a <span className="text-primary font-medium">{sessionData.path_name}</span> plan.</>
            ) : (
              <>Your grocery list is ready to export.</>
            )}
          </p>

          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              size="sm" 
              className="rounded-full flex-1"
              onClick={handleContinue}
              data-testid="toast-continue"
            >
              Continue
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="rounded-full"
              onClick={handleStartNew}
              data-testid="toast-start-new"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              New plan
            </Button>
          </div>
        </div>

        {/* Progress bar for auto-dismiss */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted rounded-b-xl overflow-hidden">
          <div 
            className="h-full bg-primary/30 animate-shrink-width"
            style={{ animationDuration: '12s' }}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionRecoveryToast;
